import React from "react";
import Cropper from "react-easy-crop";
import { getOrientation } from "get-orientation/browser";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slider,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";

import { useServices, useSnackbar } from "contexts";
import { updateProfilePicture } from "services";
import { getCroppedImg, getRotatedImage } from "utils";

/**
 * Crop-and-upload dialog for the profile picture — the mobile port of
 * `dialogs/ProfileUploadDialog`.
 *
 * Identical pipeline to desktop (read file → correct EXIF rotation → square
 * crop via react-easy-crop → `updateProfilePicture`, which also refreshes the
 * session user). Only the layout differs: a bottom-anchored sheet with a
 * shorter crop area and full-width actions, sized for a phone.
 *
 * `onClose` is called with the new profile URL on success, and with nothing
 * when the user dismisses the dialog — the same contract as desktop.
 */

// EXIF orientation → the rotation needed to display the photo upright.
const ORIENTATION_TO_ANGLE = {
  3: 180,
  6: 90,
  8: -90,
};

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export default function ProfilePictureDialog({ open, onClose = () => {} }) {
  const service = useServices();
  const snackbar = useSnackbar();

  const [imageSrc, setImageSrc] = React.useState(null);
  const [imageType, setImageType] = React.useState("");
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [rotation, setRotation] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // Reset every time the sheet opens so a previous pick never leaks in.
  React.useEffect(() => {
    if (open) {
      setImageSrc(null);
      setImageType("");
      setCrop({ x: 0, y: 0 });
      setRotation(0);
      setZoom(1);
      setCroppedAreaPixels(null);
      setSaving(false);
    }
  }, [open]);

  const onCropComplete = React.useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageType(file.type);

    let imageDataUrl = await readFile(file);

    // Phone cameras lean on EXIF for orientation — apply it before cropping.
    const orientation = await getOrientation(file);
    const exifRotation = ORIENTATION_TO_ANGLE[orientation];
    if (exifRotation) {
      imageDataUrl = await getRotatedImage(imageDataUrl, exifRotation);
    }

    setImageSrc(imageDataUrl);

    // Allow re-picking the same file (change events don't fire otherwise).
    e.target.value = "";
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
        {},
        imageType
      );

      const result = await service.callService(updateProfilePicture, croppedImage);

      onClose(result.profileUrl);
    } catch (error) {
      console.error(error);
      snackbar.error("Uh Oh! Unable to upload photo. Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullWidth
      PaperProps={{
        sx: {
          m: 0,
          width: "100%",
          maxWidth: "100%",
          position: "fixed",
          bottom: 0,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          pt: 4,
        }}
      >
        <Typography variant="h6-semiBold" color="neutral.clr-800">
          Edit your profile
        </Typography>
        <IconButton onClick={() => onClose()} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, pb: 4 }}>
        {imageSrc ? (
          <>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 240,
                background: "#333",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={1 / 1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </Box>

            <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Typography
                  variant="body02-medium"
                  color="neutral.clr-600"
                  sx={{ minWidth: 64 }}
                >
                  Zoom
                </Typography>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-label="Zoom"
                  onChange={(_, value) => setZoom(value)}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Typography
                  variant="body02-medium"
                  color="neutral.clr-600"
                  sx={{ minWidth: 64 }}
                >
                  Rotation
                </Typography>
                <Slider
                  track={false}
                  value={rotation}
                  min={-180}
                  max={180}
                  step={1}
                  aria-label="Rotation"
                  onChange={(_, value) => setRotation(value)}
                />
              </Box>
            </Box>
          </>
        ) : (
          <Box
            component="label"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              height: 200,
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "neutral.clr-400",
              bgcolor: "neutral.clr-50",
              cursor: "pointer",
            }}
          >
            <AddPhotoAlternateOutlinedIcon
              sx={{ fontSize: 36, color: "primary.clr-300" }}
            />
            <Typography variant="body01-semiBold" color="primary.clr-300">
              Choose a photo
            </Typography>
            <Typography variant="body02-regular" color="neutral.clr-500">
              PNG or JPG
            </Typography>
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={onFileChange}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 3, mt: 5 }}>
          <Button
            variant="outlined"
            onClick={() => onClose()}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={saving}
            disabled={!imageSrc}
            onClick={handleSave}
            sx={{ flex: 1 }}
          >
            Save
          </LoadingButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
